import type { ReactNode } from 'react';

import { cn } from '@/lib/utils';

/**
 * Section frame for generator pages: eyebrow + display heading + measured
 * description, then content. `tone="band"` sets the section on a soft
 * full-width panel to break the page into rhythm.
 */
export function ToolSection({
  id,
  eyebrow,
  title,
  description,
  align = 'start',
  tone = 'plain',
  aside,
  children,
}: {
  id?: string;
  eyebrow?: string;
  title: string;
  description?: ReactNode;
  align?: 'start' | 'center';
  tone?: 'plain' | 'band';
  /** Content beside the header on large screens (e.g. an FAQ contact note). */
  aside?: ReactNode;
  children: ReactNode;
}) {
  const header = (
    <div className={cn('space-y-3', align === 'center' && 'mx-auto max-w-2xl text-center')}>
      {eyebrow ? (
        <p className="text-xs font-semibold uppercase tracking-[0.14em] text-primary">{eyebrow}</p>
      ) : null}
      <h2 className="text-balance text-2xl font-bold tracking-tight text-foreground sm:text-3xl">{title}</h2>
      {description ? (
        <p className={cn('max-w-2xl text-pretty text-base leading-7 text-muted-foreground', align === 'center' && 'mx-auto')}>
          {description}
        </p>
      ) : null}
      {aside}
    </div>
  );

  return (
    <section
      id={id}
      className={cn(
        'scroll-mt-24',
        tone === 'band' &&
          'rounded-2xl bg-muted/45 px-5 py-12 ring-1 ring-border/60 sm:px-10 sm:py-16 dark:bg-white/[0.04] dark:ring-white/10',
      )}
    >
      <div className="space-y-10">
        {header}
        {children}
      </div>
    </section>
  );
}

/** Two-column section: sticky header on the left, content on the right (lg+). */
export function ToolSplitSection({
  id,
  eyebrow,
  title,
  description,
  aside,
  children,
}: {
  id?: string;
  eyebrow?: string;
  title: string;
  description?: ReactNode;
  aside?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section id={id} className="scroll-mt-24">
      <div className="grid gap-10 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] lg:gap-16">
        <div className="space-y-3 lg:sticky lg:top-24 lg:self-start">
          {eyebrow ? (
            <p className="text-xs font-semibold uppercase tracking-[0.14em] text-primary">{eyebrow}</p>
          ) : null}
          <h2 className="text-balance text-2xl font-bold tracking-tight text-foreground sm:text-3xl">{title}</h2>
          {description ? (
            <p className="text-pretty text-base leading-7 text-muted-foreground">{description}</p>
          ) : null}
          {aside}
        </div>
        <div>{children}</div>
      </div>
    </section>
  );
}
